// Registry of the OpenAPI documents the docs page can render, keyed by a stable spec id that appears
// in the page's `spec` query parameter. Adding a partner interface is one entry here — no change to
// the viewer, the print view, or either app's routing.
//
// Specs are loaded on demand so each one gets its own bundle chunk and only the spec actually being
// viewed is fetched. The JSON files are generated from the firmware contract types and drift-guarded
// by firmware tests — do not hand-edit them.

// Enums imported from their generated modules rather than the @app/shared barrel: this module is pulled in
// by anything that merely LINKS to the docs, so it must stay as light as the link sites expect.
import { ClientEndpointType } from "@app/shared/src/models/Shared.Models.Api.V1.ClientEndpoints.ClientEndpointType.gen";
import { ClientEndpointJsonVersion } from "@app/shared/src/models/ClientEndpointJsonVersion.gen";
import { endpointApiDocsPath, endpointApiDocsUrl } from "./paths";

/** The subset of OpenAPI 3.1 the viewer and print view read. Everything else passes through. */
export type OpenApiDocument = {
    openapi?: string;
    info?: { title?: string; version?: string; description?: string };
    webhooks?: Record<string, any>;
    paths?: Record<string, any>;
    components?: { schemas?: Record<string, any>; securitySchemes?: Record<string, any> };
    [key: string]: any;
};

export type ApiDocsSpecEntry = {
    /** Stable id used in the docs route's query string; also the downloaded file's name stem. */
    id: string;
    /**
     * Short human label for the page's spec picker. Held here rather than read from the document's
     * `info.title` because the picker lists every spec while only the selected one is loaded — deriving
     * labels from the documents would mean fetching all of them and defeat the per-spec code splitting.
     * Keep it in sync with the document's own title.
     */
    label: string;
    load: () => Promise<OpenApiDocument>;
};

// Keyed by "<endpoint type>" or "<endpoint type>-v<payload version>" where a type has more than one wire
// version. An endpoint type that later gains a version keeps its existing id working — add the new version
// alongside rather than renaming, because these ids are in URLs partners have been given.
export const apiDocsSpecs: Record<string, ApiDocsSpecEntry> = {
    "json-v2": { id: "json-v2", label: "JSON Push Interface (v2)", load: () => import("../../../assets/json/json-v2-openapi.json").then((m) => m.default as OpenApiDocument) },
    "json-v1": { id: "json-v1", label: "JSON Push Interface (v1)", load: () => import("../../../assets/json/json-v1-openapi.json").then((m) => m.default as OpenApiDocument) },
    drakewell: { id: "drakewell", label: "Drakewell Push Interface", load: () => import("../../../assets/drakewell/drakewell-openapi.json").then((m) => m.default as OpenApiDocument) },
    ms2: { id: "ms2", label: "MS2 Traffic Data Feed", load: () => import("../../../assets/ms2/ms2-openapi.json").then((m) => m.default as OpenApiDocument) },
    fabric: { id: "fabric", label: "Microsoft Fabric Event Stream", load: () => import("../../../assets/fabric/fabric-openapi.json").then((m) => m.default as OpenApiDocument) },
    // Not a client endpoint: device-health alert webhooks are configured under Notifications rather than
    // Integrations, so nothing maps to this id from apiDocsSpecIdForEndpoint — the Webhooks tab links to
    // it directly. Registered here so it shares the page, the viewer and the spec download. Kept last so
    // the picker lists the partner push interfaces together.
    "alert-webhook": { id: "alert-webhook", label: "Device Health Alert Webhook", load: () => import("../../../assets/alerts/alert-webhook-openapi.json").then((m) => m.default as OpenApiDocument) },
};

/**
 * What the page shows when the query string names no spec. Named explicitly rather than derived from key
 * order so that registering a new interface can't silently change what the bare route renders.
 */
export const defaultApiDocsSpecId = "json-v2";

/** Every registered spec, in registry order — the picker's option list. */
export const apiDocsSpecList: ApiDocsSpecEntry[] = Object.values(apiDocsSpecs);

/** True when the id names a spec we actually publish. Lets callers tell "no id given" from "bad id given". */
export const isKnownApiDocsSpecId = (specId: string | null | undefined): boolean => !!specId && specId in apiDocsSpecs;

export const resolveApiDocsSpec = (specId: string | null | undefined): ApiDocsSpecEntry =>
    (specId ? apiDocsSpecs[specId] : undefined) ?? apiDocsSpecs[defaultApiDocsSpecId];

/**
 * Minimal endpoint shape needed to choose a spec. Structural rather than the endpoint model, so linking to
 * the docs from a table row or an editor costs nothing beyond this module.
 */
export type ApiDocsEndpointRef = {
    endpointType?: ClientEndpointType;
    jsonVersion?: ClientEndpointJsonVersion;
};

/**
 * Spec id documenting an endpoint's push interface, or undefined when we publish no docs for its type — add
 * a case as an endpoint type grows a spec. A type whose wire shape is version-selected resolves per version,
 * so a link always lands on the document that endpoint actually sends rather than on whichever version
 * happens to be registered first.
 *
 * Lives here rather than in paths.ts because this module already owns the ids: a mapping to an id that no
 * longer exists would otherwise silently fall back to the default spec, i.e. show the wrong partner's docs.
 */
export const apiDocsSpecIdForEndpoint = (endpoint: ApiDocsEndpointRef | undefined): string | undefined => {
    const specId = ((): string | undefined => {
        switch (endpoint?.endpointType) {
            case ClientEndpointType.Drakewell:
                return "drakewell";
            case ClientEndpointType.MS2:
                return "ms2";
            case ClientEndpointType.Fabric:
                return "fabric";
            case ClientEndpointType.Json:
                return endpoint.jsonVersion === ClientEndpointJsonVersion.V1 ? "json-v1" : "json-v2";
            default:
                return undefined;
        }
    })();
    // Only advertise a link the registry can actually serve, so a stale mapping hides the link instead of
    // rendering someone else's interface.
    return specId && specId in apiDocsSpecs ? specId : undefined;
};

/** Route + query string for an endpoint's docs, or undefined when its type has none. */
export const apiDocsPathForEndpoint = (endpoint: ApiDocsEndpointRef | undefined): string | undefined => {
    const specId = apiDocsSpecIdForEndpoint(endpoint);
    return specId ? endpointApiDocsPath(specId) : undefined;
};

/**
 * Href to an endpoint's docs from a link site, or undefined when its type has none — or when the host can't
 * route to the page and hasn't said where it lives.
 *
 * `docsOrigin` is for hosts that don't bundle the docs page themselves (the device WebConfig — see
 * paths.ts). Passing it switches the link to an absolute URL, because a relative path would just 404 there.
 * The origin comes from the device's own `/settings` (`dashboardUrl`, derived from `ApiHelper.DashboardUrl`)
 * so staging devices link to the staging dashboard.
 *
 * `hostsDocsPage: false` without a `docsOrigin` yields undefined rather than a link that would 404 — the
 * caller should render nothing.
 *
 * In the dashboard, neither is passed: the caller supplies `appBaseHref` (its router basename) and the link
 * stays in-app, working behind the device proxy.
 */
export const apiDocsHrefForEndpoint = (
    endpoint: ApiDocsEndpointRef | undefined,
    options: { docsOrigin?: string; hostsDocsPage?: boolean; appBaseHref?: string } = {}
): string | undefined => {
    const specId = apiDocsSpecIdForEndpoint(endpoint);
    if (!specId) return undefined;
    if (options.docsOrigin) return endpointApiDocsUrl(options.docsOrigin, specId);
    if (options.hostsDocsPage === false) return undefined;
    return `${(options.appBaseHref ?? "").replace(/\/$/, "")}${endpointApiDocsPath(specId)}`;
};
